/**
 * Data Fetching Hook Utilities
 *
 * Transformation and helper functions for data fetching hooks
 */

import type {
  ApiStudent,
  Student,
  ApiWordBank,
  WordBank,
  ApiWord,
  Word,
} from './types';

// ============================================================================
// Student Transformations
// ============================================================================

/**
 * Transform API student (snake_case) to frontend student (camelCase)
 */
export function transformStudent(apiStudent: ApiStudent): Student {
  return {
    id: apiStudent.id,
    teacherId: apiStudent.teacher_id,
    displayName: apiStudent.display_name,
    createdAt: apiStudent.created_at,
    updatedAt: apiStudent.updated_at,
  };
}

/**
 * Transform array of API students to frontend students
 */
export function transformStudents(apiStudents: ApiStudent[]): Student[] {
  return apiStudents.map(transformStudent);
}

// ============================================================================
// Word Transformations
// ============================================================================

/**
 * Transform API word (snake_case) to frontend word (camelCase)
 */
export function transformWord(apiWord: ApiWord): Word {
  return {
    id: apiWord.id,
    wordBankId: apiWord.word_bank_id,
    text: apiWord.text,
    phonemeCount: apiWord.phoneme_count,
    syllableTypes: apiWord.syllable_types || [],
    expectedSegmentation: apiWord.expected_segmentation || [],
    tags: apiWord.tags || [],
    createdAt: apiWord.created_at,
    updatedAt: apiWord.updated_at,
  };
}

/**
 * Transform array of API words to frontend words
 */
export function transformWords(apiWords: ApiWord[]): Word[] {
  return apiWords.map(transformWord);
}

// ============================================================================
// WordBank Transformations
// ============================================================================

/**
 * Transform API word bank (snake_case) to frontend word bank (camelCase)
 */
export function transformWordBank(apiWordBank: ApiWordBank): WordBank {
  return {
    id: apiWordBank.id,
    teacherId: apiWordBank.teacher_id,
    name: apiWordBank.name,
    createdAt: apiWordBank.created_at,
    updatedAt: apiWordBank.updated_at,
    words: apiWordBank.words ? transformWords(apiWordBank.words) : undefined,
    wordCount: apiWordBank.wordCount,
  };
}

/**
 * Transform array of API word banks to frontend word banks
 */
export function transformWordBanks(apiWordBanks: ApiWordBank[]): WordBank[] {
  return apiWordBanks.map(transformWordBank);
}

// ============================================================================
// Error Handling
// ============================================================================

/**
 * Extract error message from an error object
 */
export function extractErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'An unexpected error occurred';
}

// ============================================================================
// UUID Generation
// ============================================================================

/**
 * Generate a temporary UUID for optimistic updates
 */
export function generateTempId(): string {
  return `temp-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Check if an ID is a temporary ID (for optimistic updates)
 */
export function isTempId(id: string): boolean {
  return id.startsWith('temp-');
}
